'use client';

import Icon from '@/components/ui/AppIcon';
import LocalizedTagline from '@/components/LocalizedTagline';
import Link from 'next/link';
import { getBookingUrl } from '@/lib/booking';
import { useLocale, useLocalizedPath } from '@/context/LocaleContext';
import { getPublicStrings } from '@/i18n/public';

export default function Footer() {
  const currentYear = 2026;
  const bookingHref = getBookingUrl('footer');
  const bookingExternal = bookingHref.startsWith('http');
  const locale = useLocale();
  const localizePath = useLocalizedPath();
  const copy = getPublicStrings(locale).footer;
  const instagramUrl =
    process.env.NEXT_PUBLIC_INSTAGRAM_URL?.trim() || 'https://www.instagram.com/';
  const linkedinUrl =
    process.env.NEXT_PUBLIC_LINKEDIN_URL?.trim() ||
    'https://www.linkedin.com/in/blessing-muhwati-163b3791';

  const footerLinks = copy.links;

  const socialLinks = [
    {
      id: 'social_instagram',
      icon: 'instagram',
      href: instagramUrl,
      label: 'Instagram',
    },
    {
      id: 'social_linkedin',
      icon: 'linkedin',
      href: linkedinUrl,
      label: 'LinkedIn',
    },
    {
      id: 'social_email',
      icon: 'envelope',
      href: 'mailto:info@academyofsquash.com',
      label: 'Email',
    },
  ];

  return (
    <footer className="border-t border-border bg-foreground px-6 py-20 md:px-12">
      <div className="mx-auto max-w-7xl">
        <div className="mb-12 flex flex-col items-start justify-between gap-12 md:flex-row">
          <div className="max-w-xs">
            <div className="mb-6 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-gold">
                <Icon name="TrophyIcon" variant="solid" size={24} className="text-gold" />
              </div>
              <span className="font-heading text-xl font-bold uppercase tracking-tight text-white">
                Academy of Squash
              </span>
            </div>
            <LocalizedTagline />
            <div className="space-y-2 text-[10px] uppercase tracking-widest text-white/65">
              <p>{copy.location}</p>
              <p>info@academyofsquash.com</p>
              <p>+31 6 5896 9744</p>
            </div>
            <a
              href={bookingHref}
              {...(bookingExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              className="mt-6 inline-flex min-h-[44px] items-center justify-center bg-gold px-6 py-3 text-[10px] font-black uppercase tracking-widest text-gold-foreground hover:bg-gold/90"
            >
              {copy.bookSession}
            </a>
          </div>

          <div className="flex gap-16">
            <div className="flex flex-col gap-4">
              <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-gold">
                {copy.quickLinks}
              </p>
              {footerLinks?.slice(0, 5)?.map((link) => (
                <Link
                  key={link?.id}
                  href={localizePath(link?.href)}
                  className="text-sm text-white/80 transition-colors hover:text-white"
                >
                  {link?.label}
                </Link>
              ))}
            </div>
            <div className="flex flex-col gap-4">
              <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-gold">
                {copy.more}
              </p>
              {footerLinks?.slice(5)?.map((link) => (
                <Link
                  key={link?.id}
                  href={localizePath(link?.href)}
                  className="text-sm text-white/80 transition-colors hover:text-white"
                >
                  {link?.label}
                </Link>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-6 text-[10px] font-bold uppercase tracking-widest text-gold">
              {copy.followUs}
            </p>
            <div className="flex gap-4">
              {socialLinks?.map((social) => (
                <a
                  key={social?.id}
                  href={social?.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex h-10 w-10 items-center justify-center rounded-full border border-white/20 transition-colors hover:border-gold hover:bg-gold"
                  aria-label={social?.label}
                >
                  <Icon
                    name={
                      social?.icon === 'instagram'
                        ? 'CameraIcon'
                        : social?.icon === 'linkedin'
                          ? 'BriefcaseIcon'
                          : 'EnvelopeIcon'
                    }
                    size={18}
                    className="text-white/80 transition-colors group-hover:text-foreground"
                  />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 text-[10px] uppercase tracking-widest text-white/65 md:flex-row">
          <p>
            &copy; {currentYear} Academy of Squash. {copy.rightsReserved}
          </p>
          <div className="flex gap-8">
            <Link href={localizePath('/privacy')} className="transition-colors hover:text-white">
              {copy.privacyPolicy}
            </Link>
            <Link href={localizePath('/terms')} className="transition-colors hover:text-white">
              {copy.termsOfService}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
